/**
 * Fetch a JSON resource with one automatic retry on transient failure
 * (network blip, 5xx, CORS preflight glitch).
 *
 * Why this exists: report views (heal / scout) load several JSON blobs from
 * blob storage SAS URLs. A swallowed transient failure leaves the UI silently
 * missing data (no "After fix" button, no test list) with no diagnostic.
 * One quick retry covers the vast majority of those blips; persistent failures
 * still surface via the returned promise so callers can log + degrade.
 *
 * Honors `AbortSignal` — cancelled fetches do NOT retry.
 */

import { trackException } from "../telemetry";

const DEFAULT_RETRY_DELAY_MS = 800;

export async function fetchJsonWithRetry<T = unknown>(
    url: string,
    signal?: AbortSignal,
    opts?: { attempts?: number; retryDelayMs?: number }
): Promise<T> {
    const text = await fetchTextWithRetry(url, signal, opts);
    return JSON.parse(text) as T;
}

export async function fetchTextWithRetry(
    url: string,
    signal?: AbortSignal,
    opts?: { attempts?: number; retryDelayMs?: number }
): Promise<string> {
    const attempts = Math.max(1, opts?.attempts ?? 2);
    const delay = opts?.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

    let lastErr: unknown;
    for (let i = 0; i < attempts; i++) {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        if (i > 0) await new Promise<void>((resolve, reject) => {
            const t = setTimeout(resolve, delay);
            const onAbort = () => { clearTimeout(t); reject(new DOMException("Aborted", "AbortError")); };
            signal?.addEventListener("abort", onAbort, { once: true });
        });
        try {
            const r = await fetch(url, { signal });
            if (!r.ok) throw new Error(`HTTP ${r.status} ${r.statusText}`);
            return await r.text();
        } catch (e) {
            if (signal?.aborted || (e instanceof DOMException && e.name === "AbortError")) throw e;
            lastErr = e;
        }
    }
    const finalErr = lastErr instanceof Error ? lastErr : new Error(String(lastErr));
    trackException(finalErr, { source: "fetchJson", url: new URL(url).pathname, attempts: String(attempts) });
    throw finalErr;
}
